import { getSmallKidAgeThreshold, replacePlaceholders, RootState } from '@msc-b2c/shared-utils';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import HeadsUpInfo, { HeadsUpInfoProps, HeadsUpMessagePriority } from './HeadsUpInfo';
import { PriceBasisEnum } from '@msc-b2c/interfaces/enums';
import { GetBookingMyMscResponse, PassengerI } from '@msc-b2c/interfaces/interfaces';
import {
  hasObsCombinedRelatedPackages,
  hasSomeObsPassengersNotApplicableForPaxNotEligibleBanner,
  isAdultOrSenior,
  isUnderSmallKidAgeThreshold,
} from '../../utils/PostBookingResultsUtilities';
import styles from './headsupinfo.module.scss';

interface HeadsUpContainerProps {
  checkedPassengers?: Record<string, Record<string, boolean>>;
  bookingData?: GetBookingMyMscResponse;
}

function HeadsUpContainer({ checkedPassengers, bookingData }: Readonly<HeadsUpContainerProps>) {
  const prepaidCardType = useSelector((state: RootState) => state.myAreaCommonConfig.prepaidType);
  const headsUpMessagesConfig = useSelector(
    (state: RootState) => state.myAreaCommonConfig.headsUpMessagesConfig
  );
  const myAreaExcursionCardData = useSelector(
    (state: RootState) => state.myAreaDrawers.selectedMyAreaExcursionCard
  );
  const myAreaOBSCardData = useSelector(
    (state: RootState) => state.myAreaDrawers.selectedMyAreaOBSCard
  );
  const { shopExcursion, shopOBS } = useSelector((state: RootState) => state.myCruise);
  const [stickyHeadsUpInfo, setStickyHeadsUpInfo] = useState<HeadsUpInfoProps[]>([]);
  const [nonStickyHeadsUpInfo, setNonStickyHeadsUpInfo] = useState<HeadsUpInfoProps[]>([]);
  const smallKidAgeThreshold = getSmallKidAgeThreshold();

  const excursionPassengerPrices = useMemo(() => {
    return myAreaExcursionCardData?.code === shopExcursion?.code
      ? shopExcursion?.prices?.perPassenger || []
      : [];
  }, [myAreaExcursionCardData?.code, shopExcursion]);

  const obsPassengerPrices = useMemo(() => {
    return myAreaOBSCardData?.code === shopOBS?.code ? shopOBS?.prices?.perPassenger || [] : [];
  }, [myAreaOBSCardData?.code, shopOBS]);

  const passengerData = useMemo(() => bookingData?.passengers || [], [bookingData]);

  const isCardAllInclusive = myAreaOBSCardData?.prepaidItems?.isAllInclusive;
  const isCombinedObsPackage = hasObsCombinedRelatedPackages(myAreaOBSCardData);
  const activeShopData = prepaidCardType === 'Excursions' ? shopExcursion : shopOBS;

  const shouldShowScenario2AllInclusiveBanner = useMemo(() => {
    if (!activeShopData) {
      return false;
    }

    const prepaidItems = Array.isArray(activeShopData.prepaidItems)
      ? activeShopData.prepaidItems
      : activeShopData.prepaidItems
      ? [activeShopData.prepaidItems]
      : [];

    const hasAllInclusivePrepaidItem = prepaidItems.some((item) => item?.isAllInclusive === true);
    const hasPerCabinPricing = activeShopData.priceBasis === PriceBasisEnum.PER_CABIN;

    return hasAllInclusivePrepaidItem || hasPerCabinPricing;
  }, [activeShopData]);

  useEffect(() => {
    if (!headsUpMessagesConfig) {
      return;
    }

    const baseHeadsUp = {
      icon: headsUpMessagesConfig.icon,
      title: headsUpMessagesConfig.title,
      priority: HeadsUpMessagePriority.DEFAULT,
    };

    setStickyHeadsUpInfo([
      {
        ...baseHeadsUp,
        description: headsUpMessagesConfig.paxNotApplicableDesc,
        visibilityCondition: () =>
          (prepaidCardType === 'OBS' &&
            hasSomeObsPassengersNotApplicableForPaxNotEligibleBanner(
              obsPassengerPrices,
              isCombinedObsPackage
            )) ||
          (prepaidCardType === 'Excursions' &&
            excursionPassengerPrices.some((p) => p.isApplicable === false)),
      },
      {
        ...baseHeadsUp,
        description: headsUpMessagesConfig.allInclusiveDesc,
        visibilityCondition: () =>
          !headsUpMessagesConfig.hideAllInclusive && shouldShowScenario2AllInclusiveBanner,
      },
      {
        ...baseHeadsUp,
        description: headsUpMessagesConfig.couplePackageDesc,
        visibilityCondition: () =>
          prepaidCardType === 'OBS' &&
          !headsUpMessagesConfig.hideCouple &&
          myAreaOBSCardData?.prepaidItems?.isPerCouple,
      },
      {
        ...baseHeadsUp,
        description: headsUpMessagesConfig.nonPersonalMainDesc,
        visibilityCondition: () =>
          prepaidCardType === 'OBS' &&
          (myAreaOBSCardData?.type.code === 'PHOTO' || myAreaOBSCardData?.type.code === 'LAUNDR'),
      },
      {
        ...baseHeadsUp,
        description: headsUpMessagesConfig.combinedPackagesDesc,
        visibilityCondition: () => {
          if (
            headsUpMessagesConfig.hideCombined ||
            prepaidCardType !== 'OBS' ||
            !isCardAllInclusive
          ) {
            return false;
          }

          const hasAdultWithServiceCodes =
            passengerData.some(isAdultOrSenior) &&
            !!myAreaOBSCardData?.prepaidItems?.serviceCodes?.adults?.length;

          const hasMinorWithServiceCodes =
            passengerData.some((p) => !isAdultOrSenior(p)) &&
            !!myAreaOBSCardData?.prepaidItems?.serviceCodes?.minors?.length;

          return hasAdultWithServiceCodes || hasMinorWithServiceCodes;
        },
      },
      {
        ...baseHeadsUp,
        priority: HeadsUpMessagePriority.HIGH,
        description: replacePlaceholders(headsUpMessagesConfig.underSmallKidAgeThresholdDesc, {
          kid_yo: smallKidAgeThreshold.toString(),
        }),
        visibilityCondition: () =>
          prepaidCardType === 'Excursions' &&
          passengerData.some((passenger: PassengerI) =>
            isUnderSmallKidAgeThreshold(passenger, smallKidAgeThreshold, bookingData)
          ),
      },
    ]);
    setNonStickyHeadsUpInfo([
      {
        ...baseHeadsUp,
        description: headsUpMessagesConfig.nonPersonalSecondaryDesc,
        visibilityCondition: () =>
          (myAreaOBSCardData?.type.code === 'PHOTO' || myAreaOBSCardData?.type.code === 'LAUNDR') &&
          prepaidCardType === 'OBS' &&
          checkedPassengers != null &&
          Object.values(Object.values(checkedPassengers)[0] || {}).filter(Boolean).length > 1,
      },
    ]);
  }, [
    headsUpMessagesConfig,
    prepaidCardType,
    obsPassengerPrices,
    excursionPassengerPrices,
    isCardAllInclusive,
    shouldShowScenario2AllInclusiveBanner,
    isCombinedObsPackage,
    myAreaOBSCardData,
    checkedPassengers,
    passengerData,
    bookingData,
    smallKidAgeThreshold,
  ]);

  const visibleStickyHeadsUps = stickyHeadsUpInfo
    .filter((headsUp) => headsUp.visibilityCondition())
    .sort((headsUp) => (headsUp.priority === HeadsUpMessagePriority.HIGH ? -1 : 1));

  const visibleNonStickyHeadsUps = nonStickyHeadsUpInfo.filter((headsUp) =>
    headsUp.visibilityCondition()
  );

  return (
    <>
      <div className="headsUpContainer__wrapper">
        <div className="headsUpContainer__stickable">
          {visibleStickyHeadsUps.length > 0 && (
            <div className={`${styles['headsUpContainer__stickable__messages']}`}>
              {visibleStickyHeadsUps.map((headsUp) => (
                <HeadsUpInfo
                  key={`sticky-${headsUp.description}`}
                  {...headsUp}
                />
              ))}
            </div>
          )}
        </div>
      </div>
      <div className="headsUpContainer__non-stickable">
        {visibleNonStickyHeadsUps.map((headsUp) => (
          <HeadsUpInfo
            key={`non-sticky-${headsUp.description}`}
            {...headsUp}
          />
        ))}
      </div>
    </>
  );
}

export default HeadsUpContainer;
